/* eslint-disable react/no-danger */
import React, { useCallback, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IoChevronBack } from 'react-icons/io5';

import { getToolAction } from '../../actions/contentActions';
import { API_URL } from '../../actions';
import TagsList from '../../components/TagsList/TagsList';
import styles from './ToolView.module.scss';

function Empty({ children = 'non complété' }) {
  return <span className={styles['content-none']}>{children}</span>;
}

export default function ToolView() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const tool = useSelector((state) => state.content.tool);
  const types = useSelector((state) => state.user.tools?.types) || [];

  const load = useCallback(() => {
    if (id) getToolAction(dispatch, id);
  }, [dispatch, id]);

  useEffect(() => {
    load();
  }, [load]);

  const typeLabel = types.find((o) => o.value === tool?.type)?.label || tool?.type;
  const subtypeLabel = tool?.subtype;
  const divisionOptions = (tool?.division || []).map((d) => ({ value: d, label: d }));

  return (
    <div className={styles.container}>
      <header>
        <div className={styles.title}>
          <h2>
            {tool?.title ? `Outils / ${tool.title}` : 'Outil'}
          </h2>
          <Link to="/majee/tool" className={styles.back}>
            <IoChevronBack size={20} />
            retour à la liste
          </Link>
        </div>
      </header>

      {tool?._id && (
        <main className={styles.content}>
          <div className={styles.col}>
            <section>
              <h3>Information</h3>
              <div className={styles['input-list']}>
                <div className={styles.block}>
                  <label>Code Veeva</label>
                  <div className={`${styles.value} ${styles.code}`}>
                    {tool?.codeVeeva
                      ? <span className={styles.bold}>{tool.codeVeeva}</span>
                      : <Empty />}
                  </div>
                </div>

                <div className={styles.block}>
                  <label>Nom</label>
                  <div className={styles.value}>
                    {tool?.title
                      ? <span className={styles.bold}>{tool.title}</span>
                      : <Empty />}
                  </div>
                </div>

                <div className={styles.block}>
                  <label>Url</label>
                  <div className={styles.value}>
                    {tool?.url
                      ? (
                        <a className={styles.bold} href={tool.url} rel="noreferrer" target="_blank">
                          {tool.url}
                        </a>
                      )
                      : <Empty />}
                  </div>
                </div>

                <div className={styles.block}>
                  <label>Description</label>
                  <div className={styles.value}>
                    {tool?.description
                      ? <span className={styles.bold}>{tool.description}</span>
                      : <Empty />}
                  </div>
                </div>

                <div className={styles.block}>
                  <label>Type</label>
                  <div className={styles.value}>
                    {typeLabel
                      ? <span className={styles['selected-option']}>{typeLabel}</span>
                      : <Empty />}
                  </div>
                </div>

                {tool?.type === 'marketing' && (
                  <div className={styles.block}>
                    <label>Sous-type</label>
                    <div className={styles.value}>
                      {subtypeLabel
                        ? <span className={styles['selected-option']}>{subtypeLabel}</span>
                        : <Empty />}
                    </div>
                  </div>
                )}

                <div className={styles.block}>
                  <label>Divisions</label>
                  <div className={styles.value}>
                    <TagsList
                      isEdit={false}
                      options={divisionOptions}
                      selected={tool?.division || []}
                    />
                  </div>
                </div>

                <div className={styles.block}>
                  <label>En pratique</label>
                  <div className={styles.value}>
                    {tool?.inPractice
                      ? (
                        <div
                          className={styles.bold}
                          dangerouslySetInnerHTML={{ __html: tool.inPractice }}
                        />
                      )
                      : <Empty />}
                  </div>
                </div>

                <div className={styles.block}>
                  <label>Bon à savoir</label>
                  <div className={styles.value}>
                    {tool?.goodToKnow
                      ? (
                        <div
                          className={styles.bold}
                          dangerouslySetInnerHTML={{ __html: tool.goodToKnow }}
                        />
                      )
                      : <Empty />}
                  </div>
                </div>

                <div className={styles.files}>
                  <label>Fichiers</label>
                  {tool?.files?.length > 0
                    ? (
                      <div className={styles.list}>
                        {tool.files.map((f) => {
                          const url = f?.file?.path ? `${API_URL}/${f.file.path}` : null;
                          return (
                            <div key={f._id} className={styles.file}>
                              {url
                                ? (
                                  <a href={url} target="_blank" rel="noreferrer">
                                    {f.title}
                                  </a>
                                )
                                : <span>{f.title}</span>}
                            </div>
                          );
                        })}
                      </div>
                    )
                    : <p className={styles['content-none']}>Aucun fichiers</p>}
                </div>

                <div className={styles.block}>
                  <label>Formulaire</label>
                  <div className={styles.value}>
                    {tool?.textContactForm && tool?.withContactForm
                      ? (
                        <div
                          className={styles.bold}
                          dangerouslySetInnerHTML={{ __html: tool.textContactForm }}
                        />
                      )
                      : <Empty />}
                  </div>
                </div>

                <div className={styles.block}>
                  <label>Url de la vidéo</label>
                  <div className={styles.value}>
                    {tool?.urlVideo
                      ? (
                        <a className={styles.bold} href={tool.urlVideo} rel="noreferrer" target="_blank">
                          {tool.urlVideo}
                        </a>
                      )
                      : <Empty />}
                  </div>
                </div>

                <div className={styles.block}>
                  <label>Texte store</label>
                  <div className={styles.value}>
                    {tool?.textStore
                      ? (
                        <div
                          className={styles.bold}
                          dangerouslySetInnerHTML={{ __html: tool.textStore }}
                        />
                      )
                      : <Empty />}
                  </div>
                </div>

                <div className={styles.block}>
                  <label>Url de l&apos;application apple</label>
                  <div className={styles.value}>
                    {tool?.urlAppStore
                      ? (
                        <a className={styles.bold} href={tool.urlAppStore} rel="noreferrer" target="_blank">
                          {tool.urlAppStore}
                        </a>
                      )
                      : <Empty />}
                  </div>
                </div>

                <div className={styles.block}>
                  <label>Url sur google play</label>
                  <div className={styles.value}>
                    {tool?.urlGooglePlay
                      ? (
                        <a className={styles.bold} href={tool.urlGooglePlay} rel="noreferrer" target="_blank">
                          {tool.urlGooglePlay}
                        </a>
                      )
                      : <Empty />}
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className={`${styles.col} ${styles.apercu}`}>
            <section>
              <h3>Aperçu</h3>
              <div className={styles['input-list']}>
                <div className={styles.block}>
                  <label>Icone</label>
                  <br />
                  <div
                    className={styles.preview}
                    style={{ backgroundColor: tool?.color || '#E3E0F5' }}
                  >
                    {tool?.icon?.path && (
                      <img src={`${API_URL}/${tool.icon.path}`} alt="" />
                    )}
                  </div>
                </div>

                <div className={styles.block}>
                  <label>Couleur</label>
                  <div
                    className={styles.icon}
                    style={{ backgroundColor: tool?.color || '#E3E0F5' }}
                  />
                </div>

                <div className={styles.block}>
                  <label>Illustration</label>
                  <div
                    className={`${styles.preview} ${styles.illustration}`}
                    style={{ backgroundColor: tool?.color || '#E3E0F5' }}
                  >
                    {tool?.image?.path && (
                      <img src={`${API_URL}/${tool.image.path}`} alt="" />
                    )}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
      )}
    </div>
  );
}
